import { useInfiniteQuery } from 'react-query';
import { getPaymentHistoryUseCases } from '@subscriptions/composition';
import type { PaymentHistoryPageViewDto, PaymentHistoryViewDto } from '@subscriptions/application/dtos';

/** Default page size applied to all payment history infinite-scroll queries. */
export const PAYMENT_HISTORY_PAGE_SIZE = 15;

interface UsePaymentHistoryInfiniteProps {
  blockUuid?: string;
  enabled?: boolean;
}

interface UsePaymentHistoryInfiniteReturn {
  items: PaymentHistoryViewDto[];
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  isSuccess: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  refetch: () => void;
  remove: () => void;
}

/**
 * Hook for payment history with infinite scroll pagination.
 *
 * Uses React Query's useInfiniteQuery to fetch and accumulate paginated data.
 * Extracts the next page number from the backend `pagination.next` URL using
 * the `page=N` query param (mirrors the reports domain pattern).
 *
 * Works at two levels via the same signature:
 * - Organization level (no blockUuid): paginates every payment visible to the org.
 * - Block level (blockUuid provided): paginates payments of a single block.
 *
 * @param props - Optional blockUuid filter and enabled flag
 * @returns Accumulated items + query state + pagination controls
 */
export const usePaymentHistoryInfinite = ({
  blockUuid,
  enabled = true,
}: UsePaymentHistoryInfiniteProps = {}): UsePaymentHistoryInfiniteReturn => {
  const { listPaymentHistory } = getPaymentHistoryUseCases();

  const infiniteQuery = useInfiniteQuery<PaymentHistoryPageViewDto, Error>(
    ['subscriptions', 'payment-history', 'infinite', { blockUuid }],
    async ({ pageParam = 1 }) => {
      const result = await listPaymentHistory.execute({
        blockUuid,
        page: pageParam,
        pageSize: PAYMENT_HISTORY_PAGE_SIZE,
      });
      return result;
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination.next) return undefined;
        const match = lastPage.pagination.next.match(/page=(\d+)/);
        return match ? parseInt(match[1], 10) : undefined;
      },
    },
  );

  const items = infiniteQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    items,
    error: infiniteQuery.error ?? null,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isError: infiniteQuery.isError,
    isSuccess: infiniteQuery.isSuccess,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
    remove: infiniteQuery.remove,
  };
};
